import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { MediaControls } from './MediaControls'
import { alignAttribute, alignJustifyClass, type MediaAlign } from './mediaAlign'
import { getLoomEmbedUrl, isLoomEmbedUrl } from './loomUrl'

// Loom ships no TipTap extension (unlike YouTube), so this is a hand-rolled
// atom node mirroring ArticleVideo: stored as <div data-loom-video><iframe
// src="https://www.loom.com/embed/ID"> so HelpContentSanitizer's iframe
// allowlist keeps it, with an in-editor NodeView carrying the shared hover
// controls (alignment + delete). The embed plays in the editor — no click
// shield — so controls live on the node, not on selection.

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    loomVideo: {
      // Accepts a share OR embed URL; normalized to the embed form on insert.
      setLoomVideo: (options: { src: string }) => ReturnType
    }
  }
}

const IFRAME_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'

function LoomView({ node, selected, deleteNode, updateAttributes }: NodeViewProps) {
  const src = node.attrs.src ?? ''
  // Normalize a share URL to embed; if it's already a stored embed URL, use it
  // as-is (it may carry an id shorter than paste validation would accept).
  const embed = getLoomEmbedUrl(src) ?? (isLoomEmbedUrl(src) ? src : null)

  return (
    <NodeViewWrapper className={cn('group relative my-3 flex', alignJustifyClass(node.attrs.align))}>
      <div className={cn('relative w-full max-w-2xl overflow-hidden rounded-md', selected && 'ring-2 ring-primary/70')}>
        <div className="relative aspect-video w-full bg-black">
          {embed && (
            <iframe
              src={embed}
              className="absolute inset-0 h-full w-full"
              allow={IFRAME_ALLOW}
              allowFullScreen
              title="Embedded Loom video"
            />
          )}
        </div>
        <MediaControls
          align={node.attrs.align}
          onAlign={(align: MediaAlign) => updateAttributes({ align })}
          onDelete={deleteNode}
          deleteLabel="Remove Loom video"
        />
      </div>
    </NodeViewWrapper>
  )
}

export const ArticleLoom = Node.create({
  name: 'loomVideo',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      ...alignAttribute,
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-loom-video]',
        getAttrs: node => {
          const iframe = (node as HTMLElement).querySelector('iframe')
          return { src: iframe?.getAttribute('src') ?? null }
        },
      },
      // Imported/MCP-authored HTML may carry Loom's bare iframe (no
      // data-loom-video wrapper) — the sanitizer's allowlist accepts it on
      // src alone, so the editor must recognize it too or it silently
      // vanishes the next time the article is opened and saved.
      {
        tag: 'iframe[src]',
        getAttrs: node => {
          const src = (node as HTMLElement).getAttribute('src')
          return src && isLoomEmbedUrl(src) ? { src } : false
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    // src is carried by the inner iframe, not the wrapper div; align (data-align)
    // and any other attrs stay on the wrapper. Normalize to the embed form so
    // the stored URL always matches the sanitizer's loom allowlist.
    const { src, ...wrapper } = HTMLAttributes
    const embed = getLoomEmbedUrl(src ?? '') ?? src
    return [
      'div',
      mergeAttributes({ 'data-loom-video': '' }, wrapper),
      [
        'iframe',
        {
          src: embed,
          width: 640,
          height: 360,
          frameborder: '0',
          allowfullscreen: 'true',
          allow: IFRAME_ALLOW,
        },
      ],
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(LoomView)
  },

  addCommands() {
    return {
      setLoomVideo:
        options =>
        ({ commands }) => {
          const embed = getLoomEmbedUrl(options.src)
          if (!embed) return false
          return commands.insertContent({ type: this.name, attrs: { src: embed } })
        },
    }
  },
})
